import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, getServerConfig } from "@/lib/api";
import { CopyButton } from "@/pages/Applications";
import { LinkSimple, ShieldCheck, ArrowSquareOut } from "@phosphor-icons/react";

const SCOPE_OPTS = [
  { value: "read_only", label: "read_only", desc: "GET access to all resources" },
  { value: "read_write", label: "read_write", desc: "Read & write to all resources" },
  { value: "rx_read_only", label: "rx_read_only", desc: "GET access to RazorpayX" },
  { value: "rx_read_write", label: "rx_read_write", desc: "Read & write RazorpayX" },
  { value: "rx_partner_read_write", label: "rx_partner_read_write", desc: "Approve/reject payouts" },
];

function randState() { return Math.random().toString(36).slice(2, 14) + Math.random().toString(36).slice(2, 8); }

export default function AuthorizeBuilder() {
  const [params] = useSearchParams();
  const preselectAppId = params.get("app");
  const [apps, setApps] = useState([]);
  const [appId, setAppId] = useState(preselectAppId || "");
  const [clientEnv, setClientEnv] = useState("development");
  const [redirectUri, setRedirectUri] = useState("");
  const [scopes, setScopes] = useState(["read_only"]);
  const [state, setState] = useState(randState());
  const [serverConfig, setServerConfig] = useState(null);

  useEffect(() => { api.get("/applications").then(r => {
    setApps(r.data);
    if (!appId && r.data[0]) setAppId(r.data[0].id);
  }); /* eslint-disable-next-line */ }, []);

  useEffect(() => { getServerConfig().then(setServerConfig).catch(() => {}); }, []);

  const selectedApp = apps.find(a => a.id === appId);
  const client = selectedApp?.clients?.find(c => c.env === clientEnv);

  useEffect(() => {
    if (client && client.redirect_uris?.[0]) setRedirectUri(client.redirect_uris[0]);
  }, [client?.client_id]); // eslint-disable-line

  const url = useMemo(() => {
    if (!client || !redirectUri || !serverConfig) return "";
    const isLive = serverConfig.razorpay_mode === "live";
    const base = isLive
      ? `${serverConfig.razorpay_auth_base}/authorize`
      : `${window.location.origin}/oauth/authorize`;
    const u = new URL(base);
    u.searchParams.set("client_id", client.client_id);
    u.searchParams.set("response_type", "code");
    u.searchParams.set("redirect_uri", redirectUri);
    u.searchParams.set("state", state);
    // Razorpay spec: multi-scope uses array notation `scope[]=A&scope[]=B`.
    // Mock mode parses space-separated, so keep that for local consent screen.
    if (isLive) {
      if (scopes.length === 1) u.searchParams.set("scope", scopes[0]);
      else scopes.forEach(s => u.searchParams.append("scope[]", s));
    } else {
      u.searchParams.set("scope", scopes.join(" "));
    }
    return u.toString();
  }, [client, redirectUri, scopes, state, serverConfig]);

  const toggleScope = (s) => setScopes(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Step 2 · OAuth flow</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Authorize URL Builder</h1>
        <p className="mt-1.5 text-sm text-slate-500 max-w-2xl">Configure the URL you'll redirect sub-merchants to. Razorpay will return the user to your <code className="font-mono text-xs">redirect_uri</code> with a <code className="font-mono text-xs">code</code> and <code className="font-mono text-xs">state</code>.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="rounded-xl border border-[#E2E8F0] bg-white p-6 space-y-5" data-testid="builder-form">
          <Field label="Application">
            <select value={appId} onChange={(e) => setAppId(e.target.value)} data-testid="builder-app-select"
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/15">
              {apps.length === 0 && <option value="">— Create an application first —</option>}
              {apps.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Field>

          <Field label="Client">
            <div className="grid grid-cols-2 gap-2">
              {["development", "production"].map(env => (
                <button key={env} type="button" onClick={() => setClientEnv(env)} data-testid={`builder-env-${env}`}
                  className={`rounded-md border px-3 py-2 text-xs font-medium uppercase tracking-wider ${clientEnv === env ? "border-[#FF6B00] bg-[#FFF3E0] text-[#FF6B00]" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                  {env}
                </button>
              ))}
            </div>
            {client && (
              <div className="mt-2 rounded-md bg-slate-50 px-3 py-2 text-[11px] font-mono text-slate-700">{client.client_id}</div>
            )}
          </Field>

          <Field label="Redirect URI">
            <select value={redirectUri} onChange={(e) => setRedirectUri(e.target.value)} data-testid="builder-redirect"
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/15">
              {(client?.redirect_uris || []).map(u => <option key={u} value={u}>{u}</option>)}
              {(client?.redirect_uris || []).length === 0 && <option value="">No URIs configured — add some on the Application page</option>}
            </select>
          </Field>

          <Field label="Scopes">
            <div className="space-y-1.5">
              {SCOPE_OPTS.map(o => (
                <label key={o.value} className={`flex items-start gap-2.5 rounded-md border px-3 py-2 cursor-pointer ${scopes.includes(o.value) ? "border-[#FF6B00] bg-[#FFF3E0]/40" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`builder-scope-${o.value}`}>
                  <input type="checkbox" checked={scopes.includes(o.value)} onChange={() => toggleScope(o.value)} className="mt-0.5 accent-[#FF6B00]"/>
                  <div>
                    <div className="font-mono text-xs">{o.label}</div>
                    <div className="text-[11px] text-slate-500">{o.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </Field>

          <Field label="State (CSRF token)">
            <div className="flex gap-2">
              <input value={state} onChange={(e) => setState(e.target.value)} data-testid="builder-state"
                className="flex-1 rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm font-mono outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/15"/>
              <button type="button" onClick={() => setState(randState())} className="rounded-md border border-slate-200 px-3 text-xs hover:bg-slate-50" data-testid="builder-state-regen">
                Regenerate
              </button>
            </div>
            <p className="mt-1 text-[11px] text-slate-500 flex items-center gap-1"><ShieldCheck size={11}/> Store this in your DB and verify on callback.</p>
          </Field>
        </div>

        {/* Preview */}
        <div className="space-y-4">
          <div className="rounded-xl border border-[#E2E8F0] bg-white p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-base font-semibold flex items-center gap-2"><LinkSimple size={16}/> Generated URL</h3>
              {url && <CopyButton value={url} testId="copy-authorize-url" label="URL copied" />}
            </div>
            <div className="mt-3 code-block break-all" data-testid="builder-url-preview">{url || "Configure the form to generate a URL."}</div>
            {url && (
              <a href={url} target="_blank" rel="noreferrer" data-testid="open-authorize-url"
                className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-[#0F172A] px-3 py-2 text-xs font-medium text-white hover:bg-slate-800">
                Open authorize page <ArrowSquareOut size={12} />
              </a>
            )}
          </div>

          <div className="rounded-xl border border-[#E2E8F0] bg-white p-6">
            <h3 className="font-display text-base font-semibold">cURL — exchange code for token</h3>
            <p className="text-xs text-slate-500 mt-0.5">After the sub-merchant approves, you'll receive a <code className="font-mono">code</code>. Exchange it from your backend:</p>
            <div className="mt-3 code-block">{`curl -X POST ${process.env.REACT_APP_BACKEND_URL}/api/oauth/token \\
-H "Content-type: application/json" \\
-d '{
  "client_id": "${client?.client_id || "<CLIENT_ID>"}",
  "client_secret": "<CLIENT_SECRET>",
  "grant_type": "authorization_code",
  "redirect_uri": "${redirectUri || "<REDIRECT_URI>"}",
  "code": "<DECODED_CODE>",
  "mode": "${clientEnv === "production" ? "live" : "test"}"
}'`}</div>
          </div>

          {serverConfig?.razorpay_mode === "mock" && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800">
              <strong className="font-semibold">Mock mode:</strong> the authorize page is hosted on your console at <code className="font-mono">{window.location.origin}/oauth/authorize</code>. When <code className="font-mono">RAZORPAY_MODE=live</code> in backend <code className="font-mono">.env</code>, this URL automatically switches to <code className="font-mono">{serverConfig.razorpay_auth_base}/authorize</code> — no code changes needed.
            </div>
          )}
          {serverConfig?.razorpay_mode === "live" && (
            <div className="rounded-xl border border-[#D1FAE5] bg-[#D1FAE5]/40 p-4 text-xs text-[#065F46]">
              <strong className="font-semibold">Live mode:</strong> URLs point to <code className="font-mono">{serverConfig.razorpay_auth_base}</code>. Sub-merchants will see Razorpay's real consent screen.
            </div>
          )}
        </div>
      </div>

      {apps.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center">
          <p className="text-sm text-slate-500">You haven't created any applications yet.</p>
          <Link to="/applications" className="mt-3 inline-block btn-primary rounded-md px-4 py-2 text-sm font-medium">
            Create application
          </Link>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
